"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { DesignFace } from "@/components/design-face";
import { publicType, serif } from "@/components/public-type";
import { formatInr, formatProductNo, whatsappBookingUrl } from "@/lib/format";
import type { CatalogLookRecord, StudioLooks } from "@/lib/looks";

type SortKey = "featured" | "price-asc" | "price-desc" | "code";
type ViewKey = "explore" | "saved";
type IconName = "search" | "filter" | "heart" | "check" | "close" | "grid" | "chevLeft" | "chevRight" | "zoomIn" | "zoomOut";

const sorts: { id: SortKey; label: string }[] = [
  { id: "featured", label: "Collection order" },
  { id: "price-asc", label: "Price: Low → High" },
  { id: "price-desc", label: "Price: High → Low" },
  { id: "code", label: "Code A → Z" },
];

export function PublicGallery({ studio }: { studio: StudioLooks }) {
  const filterCategories = studio.categories.filter((category) => category.options.length > 0);
  const collectionCategory =
    filterCategories.find((category) => /collection/i.test(category.name)) ??
    filterCategories.find((category) => category.key === "nail-art") ??
    filterCategories[0] ??
    null;

  const [view, setView] = useState<ViewKey>("explore");
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picks, setPicks] = useState<Record<string, string[]>>({});
  const [pricePick, setPricePick] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>("featured");
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useSaved(studio.id);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 4);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const lock = filterOpen || selectionOpen || openId !== null;
    document.documentElement.classList.toggle("overflow-hidden", lock);
    return () => document.documentElement.classList.remove("overflow-hidden");
  }, [filterOpen, selectionOpen, openId]);

  useEffect(() => {
    if (!selectionOpen) setConfirmClear(false);
  }, [selectionOpen]);

  const optionMeta = useMemo(() => {
    const labels = new Map<string, { category: string; name: string }>();
    for (const category of studio.categories) {
      for (const option of category.options) labels.set(option.id, { category: category.name, name: option.name });
    }
    return labels;
  }, [studio]);

  const optionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const design of studio.designs) {
      for (const id of design.optionIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    return counts;
  }, [studio.designs]);

  const bands = useMemo(() => priceBands(studio.designs.map((design) => design.priceCents)), [studio]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase().replace(/^#/, "");
    const groups = Object.values(picks).filter((ids) => ids.length > 0);
    const chosen = bands.find((band) => band.id === pricePick) ?? null;
    const savedSet = new Set(saved);
    return studio.designs.filter((design) => {
      if (view === "saved" && !savedSet.has(design.id)) return false;
      if (chosen && (design.priceCents < chosen.min || design.priceCents >= chosen.max)) return false;
      if (!groups.every((ids) => ids.some((id) => design.optionIds.includes(id)))) return false;
      if (!needle) return true;
      const names = design.optionIds.flatMap((id) => optionMeta.get(id)?.name ?? []);
      return `${design.productNo} ${design.name} ${names.join(" ")}`.toLowerCase().includes(needle);
    });
  }, [studio.designs, view, saved, picks, bands, pricePick, query, optionMeta]);

  const hasSheetFilters = pricePick !== null || sort !== "featured" || Object.values(picks).some((ids) => ids.length > 0);

  const shown = useMemo(() => {
    const list = [...filtered];
    if (sort === "price-asc") list.sort((a, b) => a.priceCents - b.priceCents || a.productNo - b.productNo);
    else if (sort === "price-desc") list.sort((a, b) => b.priceCents - a.priceCents || a.productNo - b.productNo);
    else list.sort((a, b) => a.productNo - b.productNo);
    return list;
  }, [filtered, sort]);

  const selectedDesigns = selected.flatMap((id) => {
    const design = studio.designs.find((item) => item.id === id);
    return design ? [design] : [];
  });
  const openDesign = shown.find((design) => design.id === openId) ?? studio.designs.find((design) => design.id === openId) ?? null;
  const activeNames = filterCategories.flatMap((category) =>
    (picks[category.id] ?? []).flatMap((id) => {
      const name = category.options.find((option) => option.id === id)?.name;
      return name ? [name] : [];
    }),
  );
  const heading = activeNames.length === 1 ? activeNames[0] : view === "saved" ? "Saved" : "All designs";
  const sendUrl =
    studio.whatsapp && selectedDesigns.length > 0 ? selectionUrl(studio, selectedDesigns, optionMeta) : null;

  function togglePick(categoryId: string, optionId: string) {
    const current = picks[categoryId] ?? [];
    const next = current.includes(optionId) ? current.filter((id) => id !== optionId) : [...current, optionId];
    setPicks({ ...picks, [categoryId]: next });
  }

  function clearCategory(categoryId: string) {
    setPicks((current) => ({ ...current, [categoryId]: [] }));
  }

  function selectCollection(optionId: string | null) {
    if (!collectionCategory) return;
    setPicks((current) => ({ ...current, [collectionCategory.id]: optionId ? [optionId] : [] }));
  }

  function resetFilters() {
    setPicks({});
    setPricePick(null);
    setSort("featured");
  }

  function showAll() {
    resetFilters();
    setView("explore");
    setQuery("");
    setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleSearch() {
    setSearchOpen((open) => {
      if (open) setQuery("");
      return !open;
    });
  }

  if (studio.designs.length === 0) {
    return (
      <div className={`${publicType} grid min-h-dvh place-items-center bg-[#f8f2f4] px-6 text-center text-[#25122a]`}>
        <div className="max-w-md">
          <div className="mb-5 flex items-center justify-center gap-3.5 text-[#d2a147]">
            <span className="h-px w-10 bg-current opacity-55" />
            <span aria-hidden="true">✦</span>
            <span className="h-px w-10 bg-current opacity-55" />
          </div>
          <p className="text-[11.5px] font-semibold tracking-[0.32em] text-[#7c5816] uppercase">{studio.title}</p>
          <h1 className={`${serif} mt-3.5 text-[clamp(46px,13vw,68px)] leading-none font-normal`}>
            Nail Art <em className="text-[#d2a147]">Gallery</em>
          </h1>
          <p className="mt-4 text-[15px] text-[#7d6874]">No designs published yet.</p>
          <Link
            href="/"
            className="mt-8 inline-flex h-[52px] items-center rounded-full border border-[rgba(37,18,42,0.18)] px-6 text-sm font-semibold"
          >
            All studios
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`${publicType} catalog min-h-dvh bg-[#f8f2f4] text-[#25122a]`}>
      <header
        className={`sticky top-0 z-30 bg-[#f8f2f4]/90 backdrop-blur-xl transition-shadow ${scrolled ? "shadow-[0_1px_0_rgba(37,18,42,0.09)]" : ""}`}
      >
        <div className="mx-auto flex h-[52px] max-w-[1760px] items-center justify-between pr-1 pl-2.5 sm:pr-3 sm:pl-4 lg:h-16 lg:pr-6 lg:pl-7">
          <button type="button" onClick={showAll} className="flex min-w-0 items-center gap-2" aria-label="Show all designs">
            <span className={`${serif} truncate text-[22px] font-medium lg:text-[26px]`}>{studio.title}</span>
            <span className="shrink-0 text-[13px] text-[#d2a147]" aria-hidden="true">
              ✦
            </span>
          </button>
          <div className="flex items-center">
            <Link href={`/studios/${studio.id}`} className="px-2 text-[13px] font-semibold text-[#7d6874] hover:text-[#25122a]">
              Profile
            </Link>
            <Link href="/login" className="px-2 text-[13px] font-semibold text-[#7d6874] hover:text-[#25122a]">
              Login
            </Link>
            <IconButton label="Saved designs" className="hidden md:grid" onClick={() => { setView("saved"); setSearchOpen(false); }}>
              <Icon name="heart" />
              <CountBadge count={saved.length} />
            </IconButton>
            <IconButton label="Search designs" className="hidden md:grid" onClick={toggleSearch}>
              <Icon name="search" />
            </IconButton>
            <IconButton
              label={filterOpen ? "Hide filters" : "Filter and sort"}
              expanded={filterOpen}
              onClick={() => setFilterOpen((open) => !open)}
            >
              <Icon name="filter" />
              {hasSheetFilters ? <span className="absolute top-2.5 right-2.5 h-1.5 w-1.5 rounded-full bg-[#d2a147]" /> : null}
            </IconButton>
          </div>
        </div>

        <div className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(.22,.61,.36,1)] ${searchOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
          <div className="overflow-hidden">
            <div className="mx-auto flex max-w-[1760px] items-center gap-2 px-2.5 pt-0.5 pb-2 sm:px-4 lg:px-7">
              <label className="relative min-w-0 flex-1">
                <span className="sr-only">Search by design code or collection</span>
                <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-[#7d6874]">
                  <Icon name="search" className="h-[18px] w-[18px]" />
                </span>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  type="search"
                  placeholder="Search code or collection"
                  autoComplete="off"
                  className="h-11 w-full rounded-full border border-transparent bg-[#f3e8ec] pr-4 pl-10 text-base outline-none placeholder:text-[#a08c96] focus:border-[rgba(210,161,71,0.6)] focus:bg-white [&::-webkit-search-cancel-button]:appearance-none"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setSearchOpen(false);
                }}
                className="h-11 shrink-0 px-1.5 text-[13.5px] font-medium text-[#7d6874]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {collectionCategory ? (
          <nav aria-label={collectionCategory.name} className="mx-auto max-w-[1760px]">
            <div className="flex h-11 gap-1 overflow-x-auto px-1 [scrollbar-width:none] sm:px-3 lg:px-5 [&::-webkit-scrollbar]:hidden">
              <CollectionChip
                pressed={(picks[collectionCategory.id] ?? []).length === 0}
                onClick={() => selectCollection(null)}
              >
                All
              </CollectionChip>
              {collectionCategory.options.map((option) => (
                <CollectionChip
                  key={option.id}
                  pressed={(picks[collectionCategory.id] ?? []).includes(option.id)}
                  onClick={() => selectCollection(option.id)}
                >
                  {option.name}
                </CollectionChip>
              ))}
            </div>
          </nav>
        ) : null}
      </header>

      <main className="mx-auto max-w-[1760px] pt-1 pb-[150px] md:pb-28">
        <div className="flex min-h-10 items-center justify-between gap-3 px-2.5 pt-1 pb-2 sm:px-4 lg:px-7">
          <h2 className={`${serif} text-[30px] leading-none font-normal`}>{heading}</h2>
          <div className="flex items-center gap-3">
            {hasSheetFilters || query ? (
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  setQuery("");
                }}
                className="text-xs font-medium text-[#7d6874] underline decoration-[rgba(37,18,42,0.25)] underline-offset-4"
              >
                Clear
              </button>
            ) : null}
            <p className="text-[11px] font-semibold tracking-[0.16em] text-[#7d6874] uppercase">
              {shown.length} {shown.length === 1 ? "design" : "designs"}
            </p>
          </div>
        </div>

        {shown.length === 0 ? (
          <div className="mx-auto max-w-[420px] px-7 py-16 text-center">
            <h3 className={`${serif} text-[26px] font-normal`}>
              {view === "saved" && !hasSheetFilters && !query.trim() ? "Nothing saved yet" : "Nothing matches"}
            </h3>
            <p className="mt-2 text-sm text-[#7d6874]">
              {view === "saved" && !hasSheetFilters && !query.trim()
                ? "Tap the heart on a design to keep it here."
                : "Try another collection, or clear the filters."}
            </p>
            {hasSheetFilters || query.trim() ? (
              <button
                type="button"
                onClick={() => {
                  resetFilters();
                  setQuery("");
                }}
                className="mt-5 inline-flex h-[50px] items-center rounded-full bg-[#25122a] px-6 text-sm font-semibold text-[#f6d7dd]"
              >
                Reset
              </button>
            ) : null}
          </div>
        ) : (
          <ul className="columns-2 gap-2 px-2.5 min-[600px]:columns-3 sm:px-4 min-[960px]:columns-4 lg:gap-4 lg:px-7 min-[1360px]:columns-5">
            {shown.map((design) => (
              <GalleryTile
                key={design.id}
                design={design}
                saved={saved.includes(design.id)}
                selected={selected.includes(design.id)}
                onOpen={() => setOpenId(design.id)}
                onSave={() => setSaved((current) => toggleId(current, design.id))}
                onSelect={() => setSelected((current) => toggleId(current, design.id))}
              />
            ))}
          </ul>
        )}
      </main>

      <div
        className={`fixed left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-full bg-[#25122a] py-1 pr-1 pl-[18px] text-[#f6d7dd] shadow-[0_10px_30px_rgba(37,18,42,0.18)] transition duration-300 ${
          selected.length > 0 ? "bottom-[76px] opacity-100 md:bottom-7" : "pointer-events-none bottom-[76px] translate-y-5 opacity-0 md:bottom-7"
        }`}
        aria-live="polite"
      >
        <span className="text-[13px] font-medium whitespace-nowrap">
          <b className="font-bold text-[#e4c27a]">{selected.length}</b> {selected.length === 1 ? "design" : "designs"} selected
        </span>
        <button
          type="button"
          onClick={() => setSelectionOpen(true)}
          className="h-10 rounded-full bg-[rgba(250,248,245,0.12)] px-4 text-[12.5px] font-semibold tracking-wide hover:bg-[rgba(250,248,245,0.2)]"
        >
          View Selection
        </button>
      </div>

      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 grid h-[60px] grid-cols-3 border-t border-[rgba(37,18,42,0.09)] bg-[#f8f2f4]/90 backdrop-blur-xl md:hidden"
      >
        <NavButton
          icon="grid"
          label="Explore"
          current={view === "explore" && !searchOpen}
          onClick={() => {
            setView("explore");
            setSearchOpen(false);
          }}
        />
        <NavButton
          icon="heart"
          label="Saved"
          count={saved.length}
          current={view === "saved" && !searchOpen}
          onClick={() => {
            setView("saved");
            setSearchOpen(false);
          }}
        />
        <NavButton icon="search" label="Search" current={searchOpen} onClick={toggleSearch} />
      </nav>

      <Sheet
        open={filterOpen}
        title="Refine"
        variant="modal"
        onClose={() => setFilterOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-[50px] flex-1 items-center justify-center rounded-full border border-[rgba(37,18,42,0.18)] bg-white px-3.5 text-sm font-semibold"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="inline-flex h-[50px] flex-[1.6] items-center justify-center rounded-full bg-[#25122a] px-3.5 text-sm font-semibold text-[#f6d7dd]"
            >
              Show {shown.length} {shown.length === 1 ? "design" : "designs"}
            </button>
          </>
        }
      >
        <FilterBoard
          categories={filterCategories}
          picks={picks}
          bands={bands}
          pricePick={pricePick}
          sort={sort}
          designCount={studio.designs.length}
          optionCounts={optionCounts}
          onToggle={togglePick}
          onClear={clearCategory}
          onPrice={setPricePick}
          onSort={setSort}
        />
      </Sheet>

      <Sheet
        open={selectionOpen}
        title="Your Selection"
        subtitle={
          selectedDesigns.length === 0
            ? "Nothing selected yet"
            : `${selectedDesigns.length} ${selectedDesigns.length === 1 ? "design" : "designs"}`
        }
        onClose={() => setSelectionOpen(false)}
        footer={
          selectedDesigns.length === 0 ? null : (
            <>
              <button
                type="button"
                onClick={() => {
                  if (!confirmClear) {
                    setConfirmClear(true);
                    return;
                  }
                  setSelected([]);
                  setConfirmClear(false);
                }}
                className={`inline-flex h-[50px] flex-1 items-center justify-center rounded-full border px-3.5 text-sm font-semibold ${
                  confirmClear ? "border-[#7c5816] text-[#7c5816]" : "border-[rgba(37,18,42,0.18)]"
                }`}
              >
                {confirmClear ? "Clear all?" : "Clear Selection"}
              </button>
              {sendUrl ? (
                <a
                  href={sendUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-[50px] flex-[1.6] items-center justify-center rounded-full bg-[#25122a] px-3.5 text-sm font-semibold text-[#f6d7dd]"
                >
                  Send Selection
                </a>
              ) : (
                <p className="flex flex-[1.6] items-center text-center text-xs leading-snug text-[#7d6874]">
                  Add a WhatsApp number on the studio profile to send this selection.
                </p>
              )}
            </>
          )
        }
      >
        {selectedDesigns.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-[#7d6874]">Select designs with the check mark, then send them together.</p>
        ) : (
          <ul>
            {selectedDesigns.map((design) => {
              const tags = design.optionIds.flatMap((id) => optionMeta.get(id)?.name ?? []).slice(0, 3);
              return (
                <li key={design.id} className="flex items-center gap-3.5 border-b border-[rgba(37,18,42,0.09)] py-2.5 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectionOpen(false);
                      setOpenId(design.id);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-3.5 text-left"
                  >
                    <span className="relative h-16 w-[52px] shrink-0 overflow-hidden rounded-[10px] bg-[#efe4ea]">
                      <DesignFace name={design.name} imageUrl={design.imageUrl} swatch={null} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[14.5px] font-bold tracking-[0.06em]">{formatProductNo(design.productNo)}</span>
                      <span className="block truncate text-[12.5px] text-[#7d6874]">{tags.join(" · ") || design.name}</span>
                      <span className="mt-0.5 block text-sm font-semibold tabular-nums">{formatInr(design.priceCents)}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${formatProductNo(design.productNo)}`}
                    onClick={() => setSelected((current) => current.filter((id) => id !== design.id))}
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-[#7d6874] hover:bg-[#f3e8ec] hover:text-[#25122a]"
                  >
                    <Icon name="close" className="h-[18px] w-[18px]" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Sheet>

      {openDesign ? (
        <Viewer
          designs={
            shown.some((design) => design.id === openDesign.id)
              ? shown
              : selectedDesigns.some((design) => design.id === openDesign.id)
                ? selectedDesigns
                : [openDesign]
          }
          design={openDesign}
          optionMeta={optionMeta}
          saved={saved.includes(openDesign.id)}
          selected={selected.includes(openDesign.id)}
          onSelect={setOpenId}
          onToggleSave={() => setSaved((current) => toggleId(current, openDesign.id))}
          onToggleSelected={() => setSelected((current) => toggleId(current, openDesign.id))}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

function FilterBoard({
  categories,
  picks,
  bands,
  pricePick,
  sort,
  designCount,
  optionCounts,
  onToggle,
  onClear,
  onPrice,
  onSort,
}: {
  categories: StudioLooks["categories"];
  picks: Record<string, string[]>;
  bands: { id: string; label: string }[];
  pricePick: string | null;
  sort: SortKey;
  designCount: number;
  optionCounts: Map<string, number>;
  onToggle: (categoryId: string, optionId: string) => void;
  onClear: (categoryId: string) => void;
  onPrice: (bandId: string | null) => void;
  onSort: (sort: SortKey) => void;
}) {
  return (
    <div>
      {categories.map((category) => {
        const selected = picks[category.id] ?? [];
        return (
          <FilterGroup key={category.id} title={category.name}>
            <Opt pressed={selected.length === 0} onClick={() => onClear(category.id)}>
              All
              <small className="ml-1 text-[11px] font-medium opacity-60">{designCount}</small>
            </Opt>
            {category.options.map((option) => (
              <Opt
                key={option.id}
                pressed={selected.includes(option.id)}
                onClick={() => onToggle(category.id, option.id)}
              >
                {option.name}
                <small className="ml-1 text-[11px] font-medium opacity-60">{optionCounts.get(option.id) ?? 0}</small>
              </Opt>
            ))}
          </FilterGroup>
        );
      })}
      {bands.length > 0 ? (
        <FilterGroup title="Price">
          <Opt pressed={pricePick === null} onClick={() => onPrice(null)}>
            All prices
          </Opt>
          {bands.map((band) => (
            <Opt key={band.id} pressed={pricePick === band.id} onClick={() => onPrice(pricePick === band.id ? null : band.id)}>
              {band.label}
            </Opt>
          ))}
        </FilterGroup>
      ) : null}
      <FilterGroup title="Sort by">
        {sorts.map((item) => (
          <Opt key={item.id} pressed={sort === item.id} onClick={() => onSort(item.id)}>
            {item.label}
          </Opt>
        ))}
      </FilterGroup>
    </div>
  );
}

function CollectionChip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`relative h-full shrink-0 px-3 text-[15px] whitespace-nowrap ${pressed ? "font-semibold text-[#25122a]" : "font-medium text-[#7d6874]"}`}
    >
      {children}
      <span className={`absolute inset-x-3 bottom-1.5 h-px ${pressed ? "bg-[#d2a147]" : "bg-transparent"}`} />
    </button>
  );
}

function Opt({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`inline-flex min-h-[42px] items-center rounded-full border px-4 text-[13.5px] font-medium transition ${
        pressed
          ? "border-[#25122a] bg-[#25122a] text-[#f6d7dd]"
          : "border-[rgba(37,18,42,0.14)] bg-white text-[#25122a] hover:border-[rgba(37,18,42,0.4)]"
      }`}
    >
      {children}
    </button>
  );
}

function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-[rgba(37,18,42,0.09)] py-3.5 last:border-b-0">
      <h3 className="mb-3 text-[11px] font-bold tracking-[0.18em] text-[#7d6874] uppercase">{title}</h3>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  );
}

function IconButton({
  label,
  onClick,
  expanded,
  className = "grid",
  children,
}: {
  label: string;
  onClick: () => void;
  expanded?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
      className={`relative h-11 w-11 place-items-center rounded-full hover:bg-[rgba(37,18,42,0.05)] ${expanded ? "bg-[rgba(37,18,42,0.06)]" : ""} ${className}`}
    >
      {children}
    </button>
  );
}

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="absolute top-1 right-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[#d2a147] px-1 text-[9.5px] leading-none font-bold text-ink">
      {count}
    </span>
  );
}

function NavButton({
  icon,
  label,
  current,
  count = 0,
  onClick,
}: {
  icon: IconName;
  label: string;
  current: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={current ? "page" : undefined}
      onClick={onClick}
      className={`relative flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold tracking-[0.06em] ${current ? "text-[#25122a]" : "text-[#7d6874]"}`}
    >
      <Icon name={icon} />
      {label}
      {count > 0 ? (
        <span className="absolute top-1.5 left-[calc(50%+8px)] grid h-4 min-w-4 place-items-center rounded-full bg-[#d2a147] px-1 text-[9.5px] leading-none font-bold text-ink">
          {count}
        </span>
      ) : null}
    </button>
  );
}

function Sheet({
  open,
  title,
  subtitle,
  onClose,
  footer,
  variant = "sheet",
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  variant?: "sheet" | "modal";
  children: ReactNode;
}) {
  const [present, setPresent] = useState(open);
  const [shown, setShown] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) {
      setPresent(true);
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setPresent(false), 320);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!present) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [present]);

  if (!present) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={`absolute inset-0 bg-[rgba(28,24,22,0.45)] transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={
          variant === "modal"
            ? `absolute top-1/2 left-1/2 flex max-h-[min(760px,86dvh)] w-[min(440px,calc(100vw-32px))] -translate-x-1/2 flex-col rounded-[22px] bg-[#FFFDFB] shadow-[0_24px_80px_rgba(37,18,42,0.2)] transition duration-300 ${
                shown ? "-translate-y-1/2 opacity-100" : "translate-y-[calc(-50%+18px)] opacity-0"
              }`
            : `absolute inset-x-0 bottom-0 flex max-h-[88dvh] flex-col rounded-t-[22px] bg-[#FFFDFB] shadow-[0_-10px_40px_rgba(37,18,42,0.12)] transition duration-300 md:inset-x-auto md:top-1/2 md:bottom-auto md:left-1/2 md:w-[min(500px,calc(100vw-48px))] md:-translate-x-1/2 md:rounded-[22px] ${
                shown ? "translate-y-0 opacity-100 md:-translate-y-1/2" : "translate-y-full opacity-100 md:translate-y-[calc(-50%+18px)] md:opacity-0"
              }`
        }
      >
        <div className={`grid h-[22px] shrink-0 place-items-center ${variant === "modal" ? "" : "md:hidden"}`} aria-hidden="true">
          <span className="h-1 w-[38px] rounded-full bg-[#eadde3]" />
        </div>
        <header className={`flex shrink-0 items-center justify-between px-6 pt-0.5 pb-1.5 ${variant === "modal" ? "" : "md:pt-3.5"}`}>
          <div>
            <h2 className={`${serif} text-[28px] leading-none font-normal`}>{title}</h2>
            {subtitle ? <p className="mt-0.5 text-xs tracking-wide text-[#7d6874]">{subtitle}</p> : null}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full">
            <Icon name="close" />
          </button>
        </header>
        <div className="overflow-y-auto px-6 pt-1.5 pb-4">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 gap-2.5 border-t border-[rgba(37,18,42,0.09)] px-5 py-3">{footer}</footer>
        ) : null}
      </section>
    </div>
  );
}

function GalleryTile({
  design,
  saved,
  selected,
  onOpen,
  onSave,
  onSelect,
}: {
  design: CatalogLookRecord;
  saved: boolean;
  selected: boolean;
  onOpen: () => void;
  onSave: () => void;
  onSelect: () => void;
}) {
  return (
    <li className="tile group relative mb-2 break-inside-avoid overflow-hidden rounded-xl bg-[#efe4ea] sm:mb-3 sm:rounded-[14px] lg:mb-4">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`View ${formatProductNo(design.productNo)}, ${design.name}, ${formatInr(design.priceCents)}`}
        className="block w-full"
      >
        <span className={`relative block w-full overflow-hidden ${aspectFor(design.id)}`}>
          <span className="tile-photo absolute inset-0 block">
            <DesignFace name={design.name} imageUrl={design.imageUrl} swatch={null} />
          </span>
        </span>
      </button>
      <span className="tile-code pointer-events-none absolute bottom-2 left-2 rounded-full bg-white/80 px-2.5 py-[3px] text-[10.5px] font-semibold tracking-[0.08em] text-[#25122a] backdrop-blur-md transition duration-300">
        {formatProductNo(design.productNo)}
      </span>
      <span className="absolute top-1.5 right-1.5 flex flex-col gap-1.5">
        <button
          type="button"
          aria-pressed={saved}
          aria-label={`Save ${formatProductNo(design.productNo)}`}
          onClick={onSave}
          className={`tile-btn grid h-9 w-9 place-items-center rounded-full bg-white/85 backdrop-blur-md transition ${saved ? "text-[#d2a147]" : "text-[#25122a]"}`}
        >
          <Icon name="heart" filled={saved} className="h-[18px] w-[18px]" />
        </button>
        <button
          type="button"
          aria-pressed={selected}
          aria-label={`Select ${formatProductNo(design.productNo)}`}
          onClick={onSelect}
          className={`tile-btn grid h-9 w-9 place-items-center rounded-full backdrop-blur-md transition ${
            selected ? "bg-[#d2a147] text-ink" : "bg-white/85 text-[#25122a]"
          }`}
        >
          <Icon name="check" className="h-[18px] w-[18px]" />
        </button>
      </span>
    </li>
  );
}

function Icon({ name, filled = false, className = "h-[22px] w-[22px]" }: { name: IconName; filled?: boolean; className?: string }) {
  const props = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
  };
  if (name === "search") {
    return (
      <svg {...props}>
        <circle cx="11" cy="11" r="6.25" />
        <path d="M16 16.5 20.5 21" />
      </svg>
    );
  }
  if (name === "filter") {
    return (
      <svg {...props}>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </svg>
    );
  }
  if (name === "heart") {
    return (
      <svg {...props}>
        <path d="M12 19.4S5.2 15.2 5.2 10.4A3.7 3.7 0 0 1 12 8.2a3.7 3.7 0 0 1 6.8 2.2c0 4.8-6.8 9-6.8 9z" fill={filled ? "currentColor" : "none"} />
      </svg>
    );
  }
  if (name === "check") {
    return (
      <svg {...props}>
        <path d="M5 12.5 9.2 16.7 19 7" />
      </svg>
    );
  }
  if (name === "close") {
    return (
      <svg {...props}>
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    );
  }
  if (name === "grid") {
    return (
      <svg {...props}>
        <rect x="4" y="4" width="6.5" height="6.5" rx="1.2" />
        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" />
        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" />
        <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" />
      </svg>
    );
  }
  if (name === "chevLeft") {
    return (
      <svg {...props}>
        <path d="M14.5 5.5 8 12l6.5 6.5" />
      </svg>
    );
  }
  if (name === "chevRight") {
    return (
      <svg {...props}>
        <path d="M9.5 5.5 16 12l-6.5 6.5" />
      </svg>
    );
  }
  if (name === "zoomIn") {
    return (
      <svg {...props}>
        <circle cx="10.5" cy="10.5" r="6" />
        <path d="M15 15.5 20 20.5M10.5 8v5M8 10.5h5" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15.5 20 20.5M8 10.5h5" />
    </svg>
  );
}

function Viewer({
  designs,
  design,
  optionMeta,
  saved,
  selected,
  onSelect,
  onToggleSave,
  onToggleSelected,
  onClose,
}: {
  designs: CatalogLookRecord[];
  design: CatalogLookRecord;
  optionMeta: Map<string, { category: string; name: string }>;
  saved: boolean;
  selected: boolean;
  onSelect: (id: string) => void;
  onToggleSave: () => void;
  onToggleSelected: () => void;
  onClose: () => void;
}) {
  const [shown, setShown] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [shifted, setShifted] = useState(false);
  const directionRef = useRef<1 | -1>(1);
  const index = Math.max(0, designs.findIndex((item) => item.id === design.id));
  const indexRef = useRef(index);
  const designsRef = useRef(designs);
  indexRef.current = index;
  designsRef.current = designs;
  const tags = design.optionIds.flatMap((id) => optionMeta.get(id)?.name ?? []);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    setZoom(1);
  }, [design.id]);

  function close() {
    setShown(false);
    window.setTimeout(onClose, 280);
  }

  function step(direction: 1 | -1) {
    const list = designsRef.current;
    if (list.length < 2) return;
    const next = (indexRef.current + direction + list.length) % list.length;
    directionRef.current = direction;
    setZoom(1);
    setShifted(true);
    onSelect(list[next].id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShifted(false));
    });
  }

  const stepRef = useRef(step);
  const closeRef = useRef(close);
  stepRef.current = step;
  closeRef.current = close;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "ArrowRight") stepRef.current(1);
      else if (event.key === "ArrowLeft") stepRef.current(-1);
      else if (event.key === "Escape") closeRef.current();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className={`fixed inset-0 z-[60] bg-[#1c0e20] text-[#f6d7dd] transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}>
      <div
        className={`absolute inset-0 ${shifted ? "" : "transition-transform duration-500 ease-out"}`}
        style={{ transform: shifted ? `translateX(${directionRef.current * 12}%)` : undefined }}
      >
        <DraggableImage key={design.id} name={design.name} imageUrl={design.imageUrl} zoom={zoom} onZoom={setZoom} onSwipe={step} />
      </div>

      {designs.length > 1 ? (
        <>
          <button
            type="button"
            aria-label="Previous design"
            onClick={() => step(-1)}
            className="absolute top-1/2 left-5 z-20 hidden h-[52px] w-[52px] -translate-y-1/2 place-items-center rounded-full bg-white/10 text-[#f6d7dd] md:grid"
          >
            <Icon name="chevLeft" />
          </button>
          <button
            type="button"
            aria-label="Next design"
            onClick={() => step(1)}
            className="absolute top-1/2 right-5 z-20 hidden h-[52px] w-[52px] -translate-y-1/2 place-items-center rounded-full bg-white/10 text-[#f6d7dd] md:grid"
          >
            <Icon name="chevRight" />
          </button>
        </>
      ) : null}

      <div className="absolute inset-x-0 top-0 z-20 flex items-center gap-1.5 px-2.5 pt-2">
        <ViewerButton label="Close viewer" onClick={close}>
          <Icon name="close" />
        </ViewerButton>
        <span className="flex-1" />
        {zoom > 1 ? (
          <ViewerButton label="Zoom out" onClick={() => setZoom((value) => Math.max(1, Number((value - 0.5).toFixed(2))))}>
            <Icon name="zoomOut" />
          </ViewerButton>
        ) : null}
        <ViewerButton label="Zoom in" onClick={() => setZoom((value) => Math.min(3, Number((value + 0.5).toFixed(2))))}>
          <Icon name="zoomIn" />
        </ViewerButton>
        <ViewerButton label={saved ? "Remove saved design" : "Save design"} pressed={saved} onClick={onToggleSave}>
          <Icon name="heart" filled={saved} className={saved ? "h-[22px] w-[22px] text-[#d2a147]" : "h-[22px] w-[22px]"} />
        </ViewerButton>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 grid grid-cols-[1fr_auto] items-end gap-x-3.5 gap-y-2.5 bg-gradient-to-t from-[rgba(20,17,16,0.72)] to-transparent px-5 pt-9 pb-4">
        <div className="min-w-0">
          <p className={`${serif} text-[30px] leading-none font-medium tracking-wide`}>{formatProductNo(design.productNo)}</p>
          <p className="mt-1 text-[13px] tracking-wide text-[rgba(246,215,221,0.72)]">
            {tags.slice(0, 3).join(" · ")}
            {tags.length > 0 ? " · " : null}
            <span className="font-semibold text-[#e4c27a]">{formatInr(design.priceCents)}</span>
          </p>
          <p className="mt-0.5 truncate text-[12.5px] text-[rgba(246,215,221,0.55)]">{design.name}</p>
        </div>
        <button
          type="button"
          aria-pressed={selected}
          onClick={onToggleSelected}
          className={`pointer-events-auto inline-flex h-11 items-center gap-2 rounded-full border px-4 text-[13px] font-semibold tracking-wide ${
            selected ? "border-[#d2a147] bg-[#d2a147] text-[#1c0e20]" : "border-[rgba(246,215,221,0.34)] text-[#f6d7dd]"
          }`}
        >
          <Icon name="check" className="h-[18px] w-[18px]" />
          {selected ? "Selected" : "Select"}
        </button>
        {designs.length > 1 ? (
          <p className="col-span-2 text-center text-[11.5px] tracking-[0.2em] text-[rgba(246,215,221,0.5)] tabular-nums">
            {String(index + 1).padStart(2, "0")} / {String(designs.length).padStart(2, "0")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ViewerButton({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-[#f6d7dd] hover:bg-white/20"
    >
      {children}
    </button>
  );
}

function DraggableImage({
  name,
  imageUrl,
  zoom,
  onZoom,
  onSwipe,
}: {
  name: string;
  imageUrl: string | null;
  zoom: number;
  onZoom: (zoom: number) => void;
  onSwipe?: (direction: 1 | -1) => void;
}) {
  const surface = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const pan = useRef({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number; lx: number; ly: number; lt: number; vx: number; vy: number } | null>(null);
  const glide = useRef(0);
  const zoomRef = useRef(zoom);
  const animateZoom = useRef(true);
  const onZoomRef = useRef(onZoom);
  const auto = useRef(true);
  const zoomReady = useRef(false);
  const onSwipeRef = useRef(onSwipe);
  const gestureAuto = useRef(true);
  if (!auto.current) zoomRef.current = zoom;
  onZoomRef.current = onZoom;
  onSwipeRef.current = onSwipe;

  function write(animate: boolean) {
    const node = frame.current;
    if (!node) return;
    node.style.animation = "none";
    node.style.transition = animate ? "transform 160ms cubic-bezier(0.22, 1, 0.36, 1)" : "none";
    node.style.transform = `translate3d(${pan.current.x}px, ${pan.current.y}px, 0) scale(${zoomRef.current})`;
  }

  function stopAuto() {
    if (!auto.current) return;
    const node = frame.current;
    auto.current = false;
    if (!node) return;
    const computed = getComputedStyle(node).transform;
    if (computed && computed !== "none") {
      const matrix = new DOMMatrix(computed);
      zoomRef.current = Number(matrix.a.toFixed(2)) || zoomRef.current;
      pan.current = { x: matrix.e, y: matrix.f };
    }
    node.style.animation = "none";
    node.style.transform = computed && computed !== "none" ? computed : `scale(${zoomRef.current})`;
    onZoomRef.current(zoomRef.current);
  }

  useEffect(() => {
    const node = frame.current;
    if (!node) return;
    node.style.transformOrigin = "center center";
    node.style.animation = "gallery-zoom-in 2.8s cubic-bezier(0.22, 1, 0.36, 1) forwards";
    function finish() {
      if (!auto.current) return;
      auto.current = false;
      zoomRef.current = 1.85;
      node.style.animation = "none";
      node.style.transform = "translate3d(0px, 0px, 0px) scale(1.85)";
      animateZoom.current = false;
      onZoomRef.current(1.85);
    }
    node.addEventListener("animationend", finish);
    return () => node.removeEventListener("animationend", finish);
  }, []);

  useEffect(() => {
    if (!zoomReady.current) {
      zoomReady.current = true;
      return;
    }
    auto.current = false;
    if (zoom <= 1) pan.current = { x: 0, y: 0 };
    zoomRef.current = zoom;
    write(animateZoom.current && !drag.current);
    animateZoom.current = true;
  }, [zoom]);

  useEffect(() => {
    const node = surface.current;
    if (!node) return;
    function wheel(event: WheelEvent) {
      event.preventDefault();
      stopAuto();
      cancelAnimationFrame(glide.current);
      drag.current = null;
      const pixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * 400 : event.deltaY;
      const capped = Math.max(-90, Math.min(90, pixels));
      const next = Math.min(3, Math.max(1, Math.round((zoomRef.current - capped * 0.004) * 100) / 100));
      if (next === zoomRef.current) return;
      animateZoom.current = false;
      zoomRef.current = next;
      if (next <= 1) pan.current = { x: 0, y: 0 };
      write(false);
      onZoomRef.current(next);
    }
    node.addEventListener("wheel", wheel, { passive: false });
    return () => node.removeEventListener("wheel", wheel);
  }, []);

  useEffect(() => {
    function move(event: Event) {
      if (!(event instanceof PointerEvent)) return;
      const start = drag.current;
      const node = frame.current;
      if (!start || !node) return;
      const now = performance.now();
      const dt = Math.max(now - start.lt, 8);
      start.vx = ((event.clientX - start.lx) / dt) * 16;
      start.vy = ((event.clientY - start.ly) / dt) * 16;
      start.lx = event.clientX;
      start.ly = event.clientY;
      start.lt = now;
      pan.current = {
        x: start.px + event.clientX - start.x,
        y: start.py + event.clientY - start.y,
      };
      node.style.transform = `translate3d(${pan.current.x}px, ${pan.current.y}px, 0) scale(${zoomRef.current})`;
    }
    function release() {
      const start = drag.current;
      if (!start) return;
      const dx = start.lx - start.x;
      const dy = start.ly - start.y;
      drag.current = null;
      const swipe = Math.abs(dx) > 72 && Math.abs(dx) > Math.abs(dy) * 1.35;
      if (swipe && (gestureAuto.current || zoomRef.current <= 1.05) && onSwipeRef.current) {
        pan.current = { x: 0, y: 0 };
        auto.current = false;
        write(false);
        onSwipeRef.current(dx < 0 ? 1 : -1);
        return;
      }
      if (Math.hypot(dx, dy) > 8 && auto.current) {
        auto.current = false;
        onZoomRef.current(Number(zoomRef.current.toFixed(2)));
      }
      let vx = start.vx;
      let vy = start.vy;
      const step = () => {
        vx *= 0.9;
        vy *= 0.9;
        if (Math.hypot(vx, vy) < 0.35 || drag.current) return;
        pan.current = { x: pan.current.x + vx, y: pan.current.y + vy };
        const node = frame.current;
        if (!node) return;
        node.style.transform = `translate3d(${pan.current.x}px, ${pan.current.y}px, 0) scale(${zoomRef.current})`;
        glide.current = requestAnimationFrame(step);
      };
      glide.current = requestAnimationFrame(step);
    }
    const raw = "onpointerrawupdate" in window ? "pointerrawupdate" : "pointermove";
    window.addEventListener(raw, move, { passive: true });
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      cancelAnimationFrame(glide.current);
      window.removeEventListener(raw, move);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, []);

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    gestureAuto.current = auto.current;
    stopAuto();
    cancelAnimationFrame(glide.current);
    const node = frame.current;
    if (node) node.style.transition = "none";
    event.currentTarget.setPointerCapture(event.pointerId);
    const now = performance.now();
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      px: pan.current.x,
      py: pan.current.y,
      lx: event.clientX,
      ly: event.clientY,
      lt: now,
      vx: 0,
      vy: 0,
    };
  }

  return (
    <div
      ref={surface}
      className="absolute inset-0 cursor-grab overflow-hidden active:cursor-grabbing"
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
    >
      <div ref={frame} className="h-full w-full origin-center">
        <DesignFace name={name} imageUrl={imageUrl} swatch={null} fit="contain" />
      </div>
    </div>
  );
}

function useSaved(studioId: string) {
  const key = `gallery-saved:${studioId}`;
  const [ids, setIds] = useState<string[]>([]);
  const skip = useRef(true);

  useEffect(() => {
    skip.current = true;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      setIds(Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : []);
    } catch {
      setIds([]);
    }
  }, [key]);

  useEffect(() => {
    if (skip.current) {
      skip.current = false;
      return;
    }
    localStorage.setItem(key, JSON.stringify(ids));
  }, [ids, key]);

  return [ids, setIds] as const;
}

function aspectFor(id: string) {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) hash = (hash + id.charCodeAt(index) * (index + 1)) % 6;
  return ["aspect-[3/4]", "aspect-[4/5]", "aspect-square", "aspect-[2/3]", "aspect-[5/6]", "aspect-[3/5]"][hash];
}

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

type PriceBand = { id: string; label: string; min: number; max: number };

function priceBands(prices: number[]): PriceBand[] {
  const unique = [...new Set(prices)].sort((a, b) => a - b);
  if (unique.length < 2) return [];

  const min = unique[0];
  const max = unique[unique.length - 1];
  const steps = [5_000, 10_000, 20_000, 50_000, 100_000, 200_000, 500_000, 1_000_000];
  const step = steps.find((value) => value * 4 >= max - min) ?? 1_000_000;
  const start = Math.floor(min / step) * step;
  const bands: PriceBand[] = [];

  for (let edge = start; edge <= max; edge += step) {
    const upper = edge + step;
    if (!unique.some((price) => price >= edge && price < upper)) continue;
    bands.push({
      id: String(edge),
      label: edge === 0 ? `Under ${formatInr(upper)}` : `${formatInr(edge)} – ${formatInr(upper)}`,
      min: edge,
      max: upper,
    });
  }

  if (bands.length >= 2) return bands;

  const count = Math.min(3, unique.length);
  const size = Math.ceil(unique.length / count);
  const split: PriceBand[] = [];
  for (let index = 0; index < unique.length; index += size) {
    const slice = unique.slice(index, index + size);
    const low = slice[0];
    const high = slice[slice.length - 1];
    split.push({
      id: String(low),
      label: low === high ? formatInr(low) : `${formatInr(low)} – ${formatInr(high)}`,
      min: low,
      max: high + 1,
    });
  }
  return split.length >= 2 ? split : [];
}

function selectionUrl(
  studio: StudioLooks,
  designs: CatalogLookRecord[],
  optionMeta: Map<string, { category: string; name: string }>,
) {
  return whatsappBookingUrl(studio.whatsapp ?? "", [
    `Hi ${studio.title}, I am interested in these nail designs:`,
    ...designs.map((design) => {
      const tags = design.optionIds.flatMap((id) => optionMeta.get(id)?.name ?? []).slice(0, 4);
      return [formatProductNo(design.productNo), design.name, tags.join(", "), formatInr(design.priceCents)].filter(Boolean).join(" — ");
    }),
  ]);
}
